/**
 * How everything on the floor knows which way you are.
 *
 * One breadth-first search from wherever the player is standing, over the
 * baked navmesh, giving every sample a hop count. An enemy then walks downhill
 * — which is a flow field, and it is why twenty of them can path at once for
 * the cost of one search rather than twenty.
 *
 * Rebuilt when the player moves to a different sample or when a fifth of a
 * second has passed, whichever comes first: any more often is wasted, and any
 * less and a chaser visibly lags a corner behind.
 *
 * It is over the *baked* mesh rather than the spec's graph, so the route an
 * enemy takes is a route the geometry actually has. The two derivations
 * disagreeing is exactly the class of bug the compiler's own gates exist to
 * catch, and an enemy walking through a wall would be the first sign of it.
 */

import type { NavmeshGraph } from 'levelspec';

export class Flow {
  private readonly dist: Int32Array;
  private readonly queue: Int32Array;
  /** Which sample the player was on when this was last built. */
  private from = -1;
  private timer = 0;

  private readonly graph: NavmeshGraph;

  // Assigned in the body rather than declared as a parameter property: the
  // headless tools run this source through Node's type-stripping loader, which
  // does not implement them.
  constructor(graph: NavmeshGraph) {
    this.graph = graph;
    this.dist = new Int32Array(graph.count);
    this.queue = new Int32Array(graph.count);
    this.dist.fill(-1);
  }

  /** Hops from `node` to the player, or -1 if the search never reached it. */
  distance(node: number): number {
    return node >= 0 && node < this.dist.length ? this.dist[node] : -1;
  }

  /**
   * The neighbour of `node` that is closer to the player, or -1.
   *
   * Used one or two hops at a time rather than followed to the end: aiming at
   * a distant node means walking the straight line to it, which cuts every
   * corner in between — invisible while enemies passed through walls, and the
   * reason they wedged against them once they stopped.
   */
  downhill(node: number): number {
    const g = this.graph;
    let best = -1;
    let bestD = this.dist[node];
    for (let k = g.adjOffset[node]; k < g.adjOffset[node + 1]; k++) {
      const v = g.adjList[k];
      const d = this.dist[v];
      if (d !== -1 && d < bestD) {
        bestD = d;
        best = v;
      }
    }
    return best;
  }

  /** Rebuild from the player's position, if it is time to. */
  update(dt: number, x: number, y: number, z: number): void {
    const g = this.graph;
    this.timer -= dt;
    const node = g.locate(x, y, z, 4);
    if (node < 0) return;
    if (this.timer > 0 && node === this.from) return;
    this.timer = 0.22;
    this.from = node;

    this.dist.fill(-1);
    const q = this.queue;
    let head = 0;
    let tail = 0;
    q[tail++] = node;
    this.dist[node] = 0;
    while (head < tail) {
      const u = q[head++];
      const du = this.dist[u] + 1;
      for (let k = g.adjOffset[u]; k < g.adjOffset[u + 1]; k++) {
        const v = g.adjList[k];
        if (this.dist[v] !== -1) continue;
        this.dist[v] = du;
        q[tail++] = v;
      }
    }
  }
}
